import React from 'react';

import Chip from '@mui/material/Chip';

import { UserLink } from '@/components/UserLink';
import { isLLM } from '@/lib/utils';

import { UserAvatar } from './UserAvatar';
import type { Identity, IdentityLinkMode } from './types';

/** Defines the compact identity chip presentation options. */
export interface IdentityChipProps {
  /** Supplies the canonical identity data to render. */
  identity: Identity;
  /** Controls whether the username may render as a profile link. */
  linkMode?: IdentityLinkMode;
  /** Sets the chip avatar size. */
  avatarSize?: number;
  /** Adds an @ prefix before the username when enabled. */
  atPrefix?: boolean;
  /** Selects the MUI chip style variant. */
  variant?: 'filled' | 'outlined';
}

/** Renders a compact avatar and username chip. */
export const IdentityChip: React.FC<IdentityChipProps> = ({
  identity,
  linkMode = 'text',
  avatarSize = 20,
  atPrefix = false,
  variant = 'outlined',
}) => {
  const label = atPrefix ? `@${identity.username}` : identity.username;
  const shouldRenderAvatar = !isLLM(identity.username) && Boolean(identity.avatarUrl);

  return (
    <Chip
      variant={variant}
      size="small"
      avatar={
        shouldRenderAvatar ? (
          <UserAvatar
            username={identity.username}
            avatarUrl={identity.avatarUrl}
            size={avatarSize}
            fallbackUrl={null}
          />
        ) : undefined
      }
      label={
        <UserLink username={identity.username} disabled={linkMode === 'text'}>
          {label}
        </UserLink>
      }
    />
  );
};
